// 负责广告点击追踪
// iframe 相关的事件监听
// 页面可见性变化追踪
import { emitAdClickTelemetryV2, emitAdTelemetryV2, scheduleAdTelemetryV2 } from '@/utils/adTelemetryV2'

export default {
	data() {
		return {
			activeIframe: null,
			iframes: new Map(),
			visibilityChangeHandler: null,
			blurHandler: null,
		}
	},

	methods: {
		initAdTracking() {
			if (typeof window === 'undefined') return
			console.log('[AdTracking] 🚀 初始化点击追踪')

			this.watchForIframeInsertion()
			this.setupVisibilityTracking()
			this.setupBlurTracking()
		},

		watchForIframeInsertion() {
			// console.log('[AdTracking] 监听iframe插入')
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeType !== 1) {
							return
						}
						if (node.tagName === 'IFRAME' && node.closest('ins.adsbygoogle')) {
							// console.log('[AdTracking] ifame 广告已经检测:', node)
							this.setupIframeTracking(node)
						} else {
							const iframesInNode = node.querySelectorAll('iframe')
							iframesInNode.forEach((iframe) => {
								if (iframe.closest('ins.adsbygoogle')) {
									// console.log('[AdTracking] ifame 广告已经检测 子集:', iframe)
									this.setupIframeTracking(iframe)
								}
							})
						}
					})
				})
			})

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			})
		},

		setupIframeTracking(iframe) {
			if (this.iframes.has(iframe)) return

			const adSlot = iframe.closest('ins.adsbygoogle')?.dataset.adSlot
			console.log(`[AdTracking] 👁️ 追踪 Iframe | Slot: ${adSlot}`)
			this.iframes.set(iframe, {
				hasTracked: false,
				adSlot: adSlot,
			})
			scheduleAdTelemetryV2('status_change', 'iframe_inserted')
		},

		setupBlurTracking() {
			// console.log('[AdTracking] 焦点丢失监听')
			this.blurHandler = () => {
				if (document.activeElement && document.activeElement.tagName === 'IFRAME' && this.iframes.has(document.activeElement)) {
					// console.log('[AdTracking] 焦点丢失:', document.activeElement)
					this.handleIframeFocus(document.activeElement)
				}
			}
			window.addEventListener('blur', this.blurHandler)
		},

		handleIframeFocus(iframe) {
			// console.log('[AdTracking] 焦点获取:', iframe)
			const data = this.iframes.get(iframe)
			if (data && !data.hasTracked) {
				console.log(`[AdTracking] 🖱️ 捕获点击 | Slot: ${data.adSlot}`)
				this.trackAdClick(iframe)
				data.hasTracked = true
				this.activeIframe = iframe
			}
		},

		trackAdClick(iframe) {
			const container = iframe.closest('ins.adsbygoogle')
			if (!container) return

			const rect = container.getBoundingClientRect()
			const clickData = {
				insX: rect.left,
				insY: rect.top,
				insHeight: rect.height,
				windowWidth: window.innerWidth,
				windowHeight: window.innerHeight,
				adSlot: container.dataset.adSlot,
			}
			// console.log('[AdTracking] 广告点击数据:', clickData)

			// 基础埋点上报
			this.$adClick?.(JSON.stringify(clickData))
			this.$eventrack?.('ad_iframe_click', 'click', {
				'data-ad-slot': clickData.adSlot,
			})
			emitAdClickTelemetryV2(container, 'iframe_focus', { clickData })

			// 第三方埋点上报
			this.trackThirdPartyEvents('click', container.dataset.adSlot, clickData)
		},

		setupVisibilityTracking() {
			// console.log('[AdTracking] 页面可见性变化监听?>>')
			this.visibilityChangeHandler = () => {
				// console.log(`[AdTracking] Visibility changed to: ${document.visibilityState}`)
				if (document.visibilityState === 'visible') {
					this.activeIframe = null
				} else if (this.activeIframe) {
					const data = this.iframes.get(this.activeIframe)
					console.log(`[AdTracking] 👋 页面离开 (点击后) | Slot: ${data?.adSlot}`)
					
					// 基础埋点上报
					this.$eventrack?.('ad_iframe_click_leave', 'click', {
						'data-ad-slot': data?.adSlot,
					})
					emitAdTelemetryV2('pagehide', 'iframe_click_leave', { activeSlotId: data?.adSlot })
					
					// 第三方埋点上报
					this.trackThirdPartyEvents('click_leave', data?.adSlot, null)
				}
			}

			document.addEventListener('visibilitychange', this.visibilityChangeHandler)
		},

		cleanupAdTracking() {
			console.log('[AdTracking] 🧹 清理资源')
			if (this.visibilityChangeHandler) {
				document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
			}
			if (this.blurHandler) {
				window.removeEventListener('blur', this.blurHandler)
			}
			this.iframes.clear()
			this.activeIframe = null
		},

		// ========== 第三方埋点上报方法 ==========
		
		/**
		 * 统一的第三方埋点上报入口
		 * @param {string} eventType - 事件类型: 'click' | 'click_leave' | 'expose' | 'view'
		 * @param {string} adSlot - 广告位ID
		 * @param {Object|null} transformData - 广告位置数据 (click时提供, click_leave时为null)
		 */
		trackThirdPartyEvents(eventType, adSlot, transformData) {
			if (!adSlot) return

			// Facebook 埋点
			if (eventType === 'click' && transformData) {
				this.trackFacebookViewContent(adSlot, transformData)
			}

			// TikTok 埋点
			this.ttTrack(adSlot, eventType === 'click_leave' ? 'click' : eventType, `ad_iframe_${eventType}`)

			// Google Analytics 埋点 (占位)
			// this.trackGoogleAnalytics(eventType, adSlot, transformData)

			// 其他第三方埋点 (占位)
			// this.trackOtherThirdParty(eventType, adSlot, transformData)
		},

		/**
		 * Facebook ViewContent 事件追踪
		 * @param {string} adSlot - 广告位ID
		 * @param {Object} transformData - 广告位置数据
		 */
		trackFacebookViewContent(adSlot, transformData) {
			// 检查是否有Facebook像素配置
			const facebookConfig = this.$store?.state?.hostInject?.facebook
			if (!facebookConfig || !facebookConfig.pixelId) {
				console.log('[AdTracking] Facebook pixel not configured for this domain')
				return
			}

			// 构建ViewContent事件数据
			const contentData = {
				content_type: 'advertisement',
				content_ids: [adSlot],
				content_name: `Ad Slot ${adSlot}`,
				content_category: 'advertisement',
				currency: 'USD',
				ad_position: {
					x: transformData.insX,
					y: transformData.insY,
					height: transformData.insHeight
				},
				viewport_size: {
					width: transformData.windowWidth,
					height: transformData.windowHeight
				}
			}

			// 触发Facebook ViewContent事件
			this.$fbViewContent?.(contentData)
		},

		/**
		 * TikTok 事件追踪
		 * @param {string} id - 广告位ID
		 * @param {string} type - 事件类型: 'expose' | 'click'
		 * @param {string} name - 事件名称
		 */
		ttTrack(id, type = 'expose', name = 'ad') {
			try {
				if (typeof ttq === 'undefined') {
					console.log('[AdTracking] TikTok tracking not available')
					return
				}

				const eventName = this.$store?.state?.hostInject?.ttEvent || 'ClickButton'
				ttq.track(eventName)
			} catch (error) {
				console.log('[AdTracking] TikTok tracking error:', error)
			}
		},

		/**
		 * 其他第三方埋点 (占位)
		 * @param {string} eventType - 事件类型
		 * @param {string} adSlot - 广告位ID
		 * @param {Object|null} transformData - 广告位置数据
		 */
		// trackOtherThirdParty(eventType, adSlot, transformData) {
		// 	// TODO: 实现其他第三方埋点逻辑
		// 	// 例如: Snapchat, Pinterest, Twitter 等
		// },
	},

	beforeDestroy() {
		this.cleanupAdTracking()
	},
}
